import { useState, useEffect } from 'react';
import { useQuery } from '@apollo/react-hooks';
import memoizeOne from 'memoize-one';

import type { InvoiceRequest } from '@atlassian/commerce-ccp-gateway-client';
import { COMMERCE_BACKEND_CCP, COMMERCE_BACKEND_HAMS } from '@atlassian/commerce-ui/rollout';

import type { TrialDetailsQuery as TrialDetailsQueryType } from './graphql/__types__/TrialDetailsQuery';
import { TrialDetailsQuery } from './graphql/TrialDetailsQuery.graphql';
import { useCommerceBackendName } from './useCommerceBackendName';
import { useInvoiceRequestV2 } from './useInvoiceRequestV2';
import { useHasBillingInformation } from './useHasBillingInformation';

export interface DeactivationDateResult {
	date: Date;
	dateString: string;
}

export interface UseInvoiceRequestResponse {
	isPreDunningPeriod: boolean;
	deactivationDate: DeactivationDateResult | null;
	loading: boolean;
	error: Error | null;
}

const DAYS_TO_DEACTIVATION = 14;

/**
 * [HAMS]
 */
export const getDeactivationDate = (
	timestamp: string,
	daysToAdd?: number,
): DeactivationDateResult => {
	const date = new Date(timestamp);
	if (daysToAdd) {
		date.setDate(date.getDate() + daysToAdd);
	}

	const dateString = new Date(date).toLocaleDateString(undefined, {
		year: 'numeric',
		month: 'long',
		day: 'numeric',
	});

	return { date, dateString };
};

/**
 * [CCP] Check the invoice request data to see if it falls
 * within the pre-dunning period and has the right entitlement
 */
const getEligiblePreDunningInvoiceRequests = (
	ir: InvoiceRequest,
	entitlementId: string,
): boolean => {
	const { status, items } = ir;
	const preDunningAttempts = ir.preDunning?.preDunningAttempts;

	return (
		// determines the pre-dunning status
		status === 'OPEN' &&
		Boolean(preDunningAttempts?.length) &&
		Boolean(ir.createdAt) &&
		// @ts-ignore - outdated CCP REST autogenerated client
		!ir.invoicedAt &&
		// @ts-ignore - outdated CCP REST autogenerated client
		!ir.uninvoiceableAt &&
		// @ts-ignore - outdated CCP REST autogenerated client
		new Date() < new Date(ir.preDunning?.endAt) && // filter out non-Confluence entitlement ids
		Boolean(items?.some((item) => item.entitlementId === entitlementId))
	);
};

/**
 * Extract the invoice with the earliest startedAt date
 * to calculate the deactivation date
 */
const getEarliestPreDunningInvoiceRequest = (invoices: InvoiceRequest[]): InvoiceRequest | null => {
	if (!invoices.length) {
		return null;
	}

	return invoices.reduce((prev: InvoiceRequest, current: InvoiceRequest) => {
		// we filter for eligible invoice requests which ensures startedAt is not empty. TS doesn't like the non-null assertion (!)
		// @ts-ignore
		return prev.preDunning.startedAt < current.preDunning.startedAt ? prev : current;
	});
};

const memoizedGetEarliestPreDunningInvoiceRequest = memoizeOne(getEarliestPreDunningInvoiceRequest);

export const useIsPreDunningPeriod = (
	{ skip }: { skip: boolean } = { skip: false },
): UseInvoiceRequestResponse => {
	const [isPreDunningPeriod, setIsPreDunningPeriod] = useState(false);
	const [deactivationDate, setDeactivationDate] = useState<DeactivationDateResult | null>(null);

	const {
		commerceBackendName,
		loading: commerceBackendNameLoading,
		error: commerceBackendNameError,
	} = useCommerceBackendName(skip);

	const skipTrialDetails = skip || commerceBackendName === COMMERCE_BACKEND_CCP;

	const {
		data: trialDetailsData,
		loading: trialLoading,
		error: trialErr,
	} = useQuery<TrialDetailsQueryType>(TrialDetailsQuery, {
		// eslint-disable-next-line graphql-relay-compat/no-import-graphql-operations -- Read https://go/connie-relay-migration-fyi
		skip: skipTrialDetails,
	});

	const skipInvoiceRequest = skip || commerceBackendName === COMMERCE_BACKEND_HAMS;

	const {
		entitlementId,
		invoiceRequestData,
		loading: invoiceRequestLoading,
		error: invoiceRequestError,
	} = useInvoiceRequestV2({
		skip: skipInvoiceRequest,
	});

	//Make this call only if user is in PreDunning Period
	const {
		hasBillingInformation,
		loading: billingLoading,
		error: billingError,
	} = useHasBillingInformation(!isPreDunningPeriod || skip);

	const hasBilling = !billingLoading && !billingError && hasBillingInformation;

	const allLoading =
		commerceBackendNameLoading || billingLoading || trialLoading || invoiceRequestLoading;
	const allSkip = skipTrialDetails || skipInvoiceRequest;
	const allError = commerceBackendNameError || billingError || trialErr || invoiceRequestError;

	useEffect(() => {
		if ((!allLoading || !allError) && deactivationDate === null) {
			if (commerceBackendName === COMMERCE_BACKEND_HAMS) {
				const { trialEndDate } = trialDetailsData?.license || {};
				if (trialEndDate) {
					const deactivationDateResult = getDeactivationDate(trialEndDate, DAYS_TO_DEACTIVATION);
					const today = new Date();
					if (
						new Date(trialEndDate) < today &&
						deactivationDateResult &&
						today < deactivationDateResult.date
					) {
						setIsPreDunningPeriod(true);
						setDeactivationDate(deactivationDateResult);
					}
				}
			} else if (commerceBackendName === COMMERCE_BACKEND_CCP) {
				const data = invoiceRequestData?.data;
				if (data && entitlementId) {
					const eligibleInvoices = data.filter((invoice) =>
						getEligiblePreDunningInvoiceRequests(invoice, entitlementId),
					);
					const earliestPreDunningInvoice: InvoiceRequest | null =
						memoizedGetEarliestPreDunningInvoiceRequest(eligibleInvoices);

					// @ts-ignore - outdated CCP REST autogenerated client
					const projectedEndDate = earliestPreDunningInvoice?.preDunning?.endAt;

					if (earliestPreDunningInvoice && projectedEndDate) {
						setIsPreDunningPeriod(true);
						setDeactivationDate(getDeactivationDate(projectedEndDate));
					}
				}
			}
		}
	}, [
		allLoading,
		allError,
		commerceBackendName,
		entitlementId,
		trialDetailsData,
		invoiceRequestData,
		deactivationDate,
	]);

	if (allLoading || allError || hasBilling) {
		return {
			isPreDunningPeriod: false,
			deactivationDate: null,
			error: allError,
			loading: allLoading,
		};
	}

	return {
		isPreDunningPeriod,
		deactivationDate,
		error: allError,
		loading: allSkip ? false : allLoading,
	};
};
